import React, { useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { IconChevronLeft, IconChevronRight, IconPhoto, IconX } from "@tabler/icons-react"
import type { IChapterGalleryImage } from "@dc-ireland/shared"
import { resolveImageUrl } from "../../../lib/imageUtils"
import { chapterSectionContainerClass, chapterSectionContentClass } from "../../../lib/chapters"
import LandingEmptyState from "../LandingEmptyState"
import LandingReveal from "../LandingReveal"
import LandingSectionHeader from "../LandingSectionHeader"

type Props = {
  images: IChapterGalleryImage[]
  loading?: boolean
}

const ChapterGallerySection: React.FC<Props> = ({ images, loading = false }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const prefersReducedMotion = useReducedMotion()

  const openLightbox = (index: number) => setLightboxIndex(index)
  const closeLightbox = () => setLightboxIndex(null)

  const navigate = (dir: -1 | 1) => {
    if (lightboxIndex === null) return
    setLightboxIndex((lightboxIndex + dir + images.length) % images.length)
  }

  return (
    <section id="gallery" className="scroll-mt-28 bg-[#fafafa] py-16 md:py-24">
      <div className={chapterSectionContainerClass}>
        <LandingSectionHeader
          eyebrow="Life Together"
          title="Gallery"
          description="Moments from worship, fellowship, and community across our chapter."
        />

        <div className={chapterSectionContentClass}>
          {loading ? (
            <p className="text-center font-dm-sans text-sm text-[#5a5a5a]">Loading gallery…</p>
          ) : images.length === 0 ? (
            <LandingEmptyState
              icon={IconPhoto}
              pattern="sparkle"
              title="No gallery photos yet"
              description="Photos from this chapter will appear here as they're added."
            />
          ) : (
            <div className="columns-2 gap-3 md:columns-3 md:gap-4">
              {images.map((image, index) => (
                <LandingReveal key={image.id ?? `${image.src}-${index}`} delay={index * 0.08}>
                  <button
                    type="button"
                    onClick={() => openLightbox(index)}
                    className="group mb-3 block w-full overflow-hidden rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9e7e43] md:mb-4"
                  >
                    <img
                      src={resolveImageUrl(image.src)}
                      alt={image.alt}
                      className="w-full object-cover transition duration-500 group-hover:scale-105"
                      style={{ breakInside: "avoid" }}
                    />
                  </button>
                </LandingReveal>
              ))}
            </div>
          )}
        </div>
      </div>

      {lightboxIndex !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute right-4 top-4 rounded-full p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
            aria-label="Close gallery"
          >
            <IconX size={24} />
          </button>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(-1) }}
            className="absolute left-4 rounded-full p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
            aria-label="Previous image"
          >
            <IconChevronLeft size={28} />
          </button>

          <motion.img
            key={lightboxIndex}
            src={resolveImageUrl(images[lightboxIndex].src)}
            alt={images[lightboxIndex].alt}
            initial={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="max-h-[85vh] max-w-full rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(1) }}
            className="absolute right-4 rounded-full p-2 text-white/80 transition hover:bg-white/10 hover:text-white md:right-16"
            aria-label="Next image"
          >
            <IconChevronRight size={28} />
          </button>
        </motion.div>
      )}
    </section>
  )
}

export default ChapterGallerySection
